import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Edit, CalendarDays, MapPin, Link2, Music2 } from 'lucide-react'
import {
  useEvent,
  useEventLineup,
  useEventSetlist,
} from '@/hooks/use-events'
import { useAllMusicTracks, useCoverSongs } from '@/hooks/use-music'
import { formatDate } from '@/lib/utils'
import type { Tables } from '@/types/supabase'

type SetlistItem = Tables<'event_setlist'>

function SectionTitle({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-3 mb-4">
      <h2 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider whitespace-nowrap">
        {children}
      </h2>
      <div className="h-px flex-1 bg-border" />
    </div>
  )
}

function formatEventDateRange(startsAt: string, endsAt: string | null): string {
  const start = formatDate(startsAt, { dateStyle: 'full', timeStyle: undefined })
  if (!endsAt) return start
  const startDay = new Date(startsAt).toDateString()
  const endDay = new Date(endsAt).toDateString()
  if (startDay === endDay) return start
  return `${start} – ${formatDate(endsAt, { dateStyle: 'full', timeStyle: undefined })}`
}

export default function EventDetailPage() {
  const { id } = useParams()
  const navigate = useNavigate()

  const { data: event, isLoading } = useEvent(id)
  const { data: lineup = [] } = useEventLineup(id)
  const { data: setlist = [] } = useEventSetlist(id)
  const { data: allTracks = [] } = useAllMusicTracks()
  const { data: coverSongs = [] } = useCoverSongs()

  if (isLoading) return <p className="text-muted-foreground">Memuat event...</p>
  if (!event) return <p className="text-muted-foreground">Event tidak ditemukan.</p>

  const trackMap = new Map(allTracks.map((t) => [t.id, t]))
  const coverMap = new Map(coverSongs.map((c) => [c.id, c]))

  function resolveSongLabel(item: SetlistItem): string {
    if (item.song_type === 'track' && item.track_id) {
      const track = trackMap.get(item.track_id)
      if (track) {
        const album = track.music_releases?.title
        return album ? `${track.title} — ${album}` : track.title
      }
    }
    if (item.song_type === 'cover' && item.cover_id) {
      const cover = coverMap.get(item.cover_id)
      if (cover) return `${cover.title} (${cover.original_artist})`
    }
    return '—'
  }

  return (
    <div className="max-w-3xl space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <button
          type="button"
          onClick={() => navigate('/events')}
          className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors shrink-0"
        >
          <ArrowLeft className="h-5 w-5" />
        </button>
        <button
          type="button"
          onClick={() => navigate(`/events/${id}/edit`)}
          className="flex items-center gap-1.5 rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <Edit className="h-4 w-4" />
          Edit
        </button>
      </div>

      {/* Cover image */}
      <div className="relative w-full aspect-video rounded-2xl overflow-hidden bg-muted border border-border">
        {event.cover_url ? (
          <img
            src={event.cover_url}
            alt={event.title}
            className="h-full w-full object-cover"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center">
            <CalendarDays className="h-16 w-16 text-muted-foreground/20" />
          </div>
        )}
        {/* Status badge */}
        <div className="absolute top-3 right-3">
          <span
            className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold shadow-md ${
              event.is_published
                ? 'bg-green-500 text-white'
                : 'bg-yellow-400 text-yellow-900'
            }`}
          >
            {event.is_published ? 'Publik' : 'Draft'}
          </span>
        </div>
      </div>

      {/* Title & meta */}
      <div className="space-y-3">
        <h1 className="text-2xl font-bold leading-tight">{event.title}</h1>
        <div className="flex flex-col gap-1.5 text-sm text-muted-foreground">
          <div className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4 shrink-0" />
            <span>{formatEventDateRange(event.starts_at, event.ends_at)}</span>
          </div>
          {(event.location || event.city || event.province) && (
            <div className="flex items-start gap-2">
              <MapPin className="h-4 w-4 shrink-0 mt-0.5" />
              <div className="space-y-0.5">
                {event.location && <span>{event.location}</span>}
                {(event.city || event.province) && (
                  <span className="block text-xs text-muted-foreground/70">
                    {[event.city, event.province].filter(Boolean).join(', ')}
                  </span>
                )}
              </div>
            </div>
          )}
          {event.online_url && (
            <div className="flex items-center gap-2">
              <Link2 className="h-4 w-4 shrink-0" />
              <a
                href={event.online_url}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:underline text-primary truncate"
              >
                {event.online_url}
              </a>
            </div>
          )}
        </div>
      </div>

      {/* Description */}
      {event.description && (
        <section>
          <SectionTitle>Deskripsi</SectionTitle>
          <p className="text-sm leading-relaxed whitespace-pre-wrap text-foreground">
            {event.description}
          </p>
        </section>
      )}

      {/* Lineup */}
      {lineup.length > 0 && (
        <section>
          <SectionTitle>Lineup ({lineup.length} member)</SectionTitle>
          <div className="flex flex-wrap gap-3">
            {lineup.map((member) => {
              const name = member.stage_name ?? member.name
              return (
                <div
                  key={member.id}
                  className="flex flex-col items-center gap-1.5 cursor-pointer"
                  onClick={() => navigate(`/members/${member.id}`)}
                >
                  <div className="h-14 w-14 rounded-full overflow-hidden bg-muted border-2 border-border flex items-center justify-center text-lg font-bold text-muted-foreground/40 select-none">
                    {member.photo_url ? (
                      <img
                        src={member.photo_url}
                        alt={name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <span>{name.charAt(0).toUpperCase()}</span>
                    )}
                  </div>
                  <span className="text-xs text-center text-muted-foreground max-w-[60px] truncate">
                    {name}
                  </span>
                </div>
              )
            })}
          </div>
        </section>
      )}

      {/* Setlist */}
      {setlist.length > 0 && (
        <section>
          <SectionTitle>Setlist ({setlist.length} lagu)</SectionTitle>
          <ol className="space-y-1.5">
            {setlist.map((item, idx) => (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-lg px-3 py-2 bg-muted/40 text-sm"
              >
                <span className="w-6 shrink-0 text-right text-muted-foreground font-mono text-xs">
                  {idx + 1}
                </span>
                <Music2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                <span className="truncate">{resolveSongLabel(item)}</span>
                {item.song_type === 'cover' && (
                  <span className="ml-auto shrink-0 rounded-full bg-blue-100 px-2 py-0.5 text-[10px] font-medium text-blue-700">
                    Cover
                  </span>
                )}
              </li>
            ))}
          </ol>
        </section>
      )}
    </div>
  )
}
